import * as Yup from "yup";
import ProductPriceModel from "../../models/products/productprice-model";
import { API_END_POINT } from "../../constants";
import { InputField } from "../../components/form/input.field.component";
import { FormikModelDialog, SubmitStatus } from "../../core/formik-model-dialog.component";
import { useEffect, useRef, useState } from "react";
import { useGetCategories } from "../../apiclient/master/useGetCategoryApi";
import { SelectField } from "../../core/select-field.component";
import { Grid, GridCellProps, GridColumn, GridItemChangeEvent } from "@progress/kendo-react-grid";
import axios from "axios";
import { getDataWithToken, getEndPoint } from "../../Utils/network.client";
import React from "react";
import '../../index.css'

interface ProductPriceEditProps {
  data: ProductPriceModel;
  isViewMode?: boolean;
  onClose?: (status: SubmitStatus, data: any) => any;
}

// Utility function for API call
const getProductPriceById = async (
  rateMainId: number,
  categoryId: number,
  effectiveDate: string | null
) => {
  try {
    const url = `${getEndPoint(API_END_POINT.PRODUCT)}/api/ProductPrice/GetProductPriceById/${rateMainId}/${categoryId}/${effectiveDate || "null"}`;
    const response = await getDataWithToken<any>(url);

    // Combine productPrices and rateSubs
    const productPrices = response?.data?.result?.productPrices || [];
    const rateSubs = response?.data?.result?.rateSubs || [];

    // Map rateSubs to productPrices
    const combinedDetails = rateSubs.map((sub: { rateMainId: any; categoryId: any; tenantId: any; }) => {
      const matchingPrice = productPrices.find(
        (price: { rateMainId: any; categoryId: any; tenantId: any; }) =>
          price.rateMainId === sub.rateMainId &&
          price.categoryId === sub.categoryId &&
          price.tenantId === sub.tenantId
      );
      return {
        ...sub,
        name: matchingPrice?.name || "",
        effectiveDate: matchingPrice?.effectiveDate || "",
      };
    });

    return combinedDetails;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error("Error fetching product price:", error.response || error.message);
    } else {
      console.error("Unexpected error:", error);
    }
    throw error;
  }
};

const ProductPriceEntry: React.FC<ProductPriceEditProps> = ({ ...props }) => {
  let { data, onClose,isViewMode } = props;
  const title = data?.rateMainId > 0 ? "Edit Product Price" : "Add Product Price";
  const [productPriceDetails, setProductPriceDetails] = useState<ProductPriceModel[]>([]);
  const [loadingDetails, setLoadingDetails] = useState<boolean>(false);
  const [categoryId, setCategoryId] = useState<number | null>(0); // State for category
  const [effectiveDate, setEffectiveDate] = useState<string>(""); // 
  const [gridVisible, setGridVisible] = useState<boolean>(false);

  let initialValue: ProductPriceModel = {
    productPrices: [
      {
        rateMainId: data?.rateMainId || 0,
        categoryId: data?.categoryId || 0,
        name: data?.name || "",
        effectiveDate: data?.effectiveDate || "",
        tenantId: 0, // Assuming tenantId is 0, change this if needed
      },
    ],
    rateSubs: productPriceDetails.map(item => ({
      rateSubId: item.rateSubId || 0,
      effectiveDate: data?.effectiveDate || "",
      categoryId: data?.categoryId || 0,
      productId: item.productId || 0,
      code: item.code || "",
      productName: item.productName || "",
      basic: item.basic || 0,
      cgst: item.cgst || 0,
      sgst: item.sgst || 0,
      igst: item.igst || 0,
      rate: item.rate || 0,
      cardrate: item.cardrate || 0,
      freerate: item.freerate || 0,
      inEdit: true,
    })),
    rateMainId: data?.rateMainId || 0,
    effectiveDate: data?.effectiveDate || "",
    categoryId: data?.categoryId || 0,
    name: data?.name || "",
    inEdit: true,
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Required"),
  });

  const [categoryData, setCategoryData] = useState<{ value: string; label: string }[]>([]);
  const { data: fetchedCategory } = useGetCategories();

  useEffect(() => {
    if (fetchedCategory) {
      const mappedmaingroup = fetchedCategory.map((category: any) => ({
        value: category.categoryId,
        label: category.name,
      }));
      setCategoryData(mappedmaingroup);
    } else {
      setCategoryData([]); // Default to an empty array if data is invalid
    }
  }, [fetchedCategory]);

  useEffect(() => {
    if (data?.rateMainId === 0 || data?.rateMainId == undefined) {
      setProductPriceDetails([]);
      setEffectiveDate("");
      setCategoryId(0);
    }
  }, [data]);

  useEffect(() => {
    if (data?.rateMainId > 0 && !loadingDetails) {
      setEffectiveDate(data?.effectiveDate);
      setCategoryId(data?.categoryId);
      setLoadingDetails(true);
      getProductPriceById(data.rateMainId, 0, "null")
        .then((response) => {
          const editableDetails = response.map((item: any) => ({
            ...item,
            cgst: Number(item.cgst) || 0,
            inEdit: true,
          }));
          setGridVisible(true);
          setProductPriceDetails(editableDetails);
          setLoadingDetails(false);
        })
        .catch((error) => {
          console.error("Failed to fetch product price details:", error);
          setProductPriceDetails([]); // Default to an empty array
          setLoadingDetails(false);
        });
    } else {
      setGridVisible(false);
    }
  }, [data, categoryId, effectiveDate]);

  const handleGetButtonClick = async () => {
    if (categoryId) {
      setLoadingDetails(true);
      try {
        const fetchedDetails = await getProductPriceById(0, categoryId, effectiveDate);
        const editableDetails = fetchedDetails.map((item: any) => ({
          ...item,
          inEdit: true, // Set the inEdit property
        }));
        setGridVisible(true);
        setProductPriceDetails(editableDetails);
        data.categoryId = categoryId;
        data.effectiveDate = effectiveDate;
      } catch (error) {
        console.error("Error fetching product price details:", error);
      } finally {
        setLoadingDetails(false);
      }
    } else {
      alert("Please select a category and effective date.");
    }
  };

  const handleCategoryChange = (value: any) => {
    setCategoryId(value);
  };

  const handleItemChange = (index: number, field: string, value: any) => {
    const updatedDetails = [...productPriceDetails];
    updatedDetails[index] = { ...updatedDetails[index], [field]: value };
    setProductPriceDetails(updatedDetails);
  };

  // Get today's date for comparison
  const today = new Date().toISOString().split('T')[0];

  // Handle if the effective date is in the past
  const isEffectiveDatePast = effectiveDate && effectiveDate < today;

  const canEdit = categoryId === 1 || !isEffectiveDatePast;


  return (
    <FormikModelDialog
      width="95%"
      title={title}
      initialData={initialValue}
      show={!!data}
      onClose={onClose}
      endpoint={API_END_POINT.PRODUCT}
      postRoute="ProductPrice/SaveProductPrice"
      showAddAnother={true}
      isViewOnly ={isViewMode}
      isUpDateEntityDetails={data?.rateMainId > 0 ? true : false}
    >
      <div>
        <div className="row">
          <div className="col-4">
            <div>Category</div>
            <div>
              <SelectField
                name="categoryId"
                options={categoryData}
                unSelectValue={0}
                notifyChange={handleCategoryChange}
                // readOnly={data?.rateMainId > 0}

              />
            </div>
          </div>

          <div className="col-3">
            <div>Effective Date</div>
            <input
              type="date"
              className="form-control"
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
              readOnly={data?.rateMainId > 0}
            />
          </div>
          <div className="col-4" 
            style ={{marginTop:"20px"}}>
            <button
              type="button"
              className="btn btn-primary ms-2"
              onClick={handleGetButtonClick}
            >
              GET
            </button>
          </div>
        </div>

        <div className="top-sec-align row">
          <h5 style={{ fontWeight: "bold" }}>Product Details</h5>
        </div>

        <table className="table table-bordered" style={{ marginTop: "10px" }}>
          <thead>
            <tr>
              <th>Code</th>
              <th>Product Name</th>
              <th>Basic</th>
              <th>CGST</th>
              <th>SGST</th>
              <th>IGST</th>
              <th>Rate</th>
              <th>Card Rate</th>
              <th>Free Rate</th>
            </tr>
          </thead>
          <tbody>
            {productPriceDetails.map((item, index) => (
              <tr key={index}>
                <td>{item.code}</td>
                <td>{item.productName}</td>
                <td>
                  <input
                    type="number"
                    maxLength={6}
                    value={item.basic === 0 ? "" : item.basic}
                    onChange={(e) => handleItemChange(index, "basic", e.target.value)}
                    className="form-control"
                    style={{ textAlign: "right" }}
                    onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    value={item.cgst === 0 ? "" : item.cgst}
                    onChange={(e) => handleItemChange(index, "cgst", e.target.value)}
                    className="form-control"
                    style={{ textAlign: "right" }}
                    onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}                    
                  />
                </td>
                <td>
                  <input
                    type="number"
                    value={item.sgst === 0 ? "" : item.sgst}
                    onChange={(e) => handleItemChange(index, "sgst", e.target.value)}
                    className="form-control"
                    style={{ textAlign: "right" }}
                    onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    value={item.igst === 0 ? "" : item.igst}
                    onChange={(e) => handleItemChange(index, "igst", e.target.value)}
                    className="form-control"
                    style={{ textAlign: "right" }}
                    onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    value={item.rate === 0 ? "" : item.rate}
                    onChange={(e) => handleItemChange(index, "rate", e.target.value)}
                    className="form-control"
                    style={{ textAlign: "right" }}
                    onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    value={item.cardrate === 0 ? "" : item.cardrate}
                    onChange={(e) => handleItemChange(index, "cardrate", e.target.value)}
                    className="form-control"
                    style={{ textAlign: "right" }}
                    // disabled={isEffectiveDatePast || categoryId !== 1} // Disable if the effective date is past or categoryId is not 1
                    disabled={!(canEdit)} // Allow editing when categoryId is 1

                    onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    value={item.freerate === 0 ? "" : item.freerate}
                    onChange={(e) => handleItemChange(index, "freerate", e.target.value)}
                    className="form-control"
                    style={{ textAlign: "right" }}
                    disabled={!(canEdit)} // Allow editing when categoryId is 1

                    // disabled={isEffectiveDatePast || categoryId !== 1} // Disable if the effective date is past or categoryId is not 1
                    onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </FormikModelDialog>
  );
};

export default ProductPriceEntry;
